<!-- Component can be used in microscope and periscope -->
<template>
  <div ref="formElement" class="decision-form">
    <ul v-if="chosen === null" class="decision-options" :data-layout="hasLines ? 'rows' : 'row'">
      <li v-for="option in options" :key="option.value" class="decision-option" :data-value="option.value">
        <!-- The tooltip sits on a wrapper: a disabled AppButton takes no pointer events, so its own title never shows. -->
        <span class="decision-option-button" :title="option.disabledReason ?? undefined">
          <AppButton
            type="button"
            :color="BUTTON_COLORS[option.tone]"
            :disabled="disabled || Boolean(option.disabledReason)"
            @click="choose(option)"
          >
            {{ option.label }}
          </AppButton>
        </span>
        <span v-if="option.line" class="decision-option-line">{{ option.line }}</span>
      </li>
    </ul>

    <div v-else class="decision-chosen" :data-tone="chosen.tone" role="alertdialog" :aria-labelledby="titleId">
      <strong :id="titleId">{{ chosen.confirmation?.title ?? chosen.label }}</strong>
      <p v-if="chosen.confirmation">{{ chosen.confirmation.text }}</p>
      <AppTextArea
        :value="comment"
        label="Kommentar"
        :hint="chosen.commentHint ?? undefined"
        :disabled="submitting || disabled"
        :rows="3"
        @input="setComment"
      />
      <div class="decision-buttons">
        <AppButton type="button" color="transparent" :disabled="submitting || disabled" @click="cancel"
          >Avbryt</AppButton
        >
        <!-- Not disabled while this form submits: a disabled button turns grey, which hides the loading
             spinner, and `loading` already blocks a second click. Disabled while another form saves. -->
        <AppButton
          type="button"
          :color="BUTTON_COLORS[chosen.tone]"
          :disabled="commentMissing || (disabled && !submitting)"
          :loading="submitting"
          @click="submit"
        >
          {{ chosen.label }}
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import AppButton from '../AppButton/AppButton.vue'
import AppTextArea from '../AppTextArea/AppTextArea.vue'
import type { DecisionOption, DecisionTone } from './AppDecisionForm.types'

// A decision, action first: the buttons, then on a click an inline form for that one decision: its
// comment (required unless the option says otherwise), what it does, Avbryt and the action, held
// until a required comment is written. The consumer makes the call on `submit` and says while it
// runs (`submitting`); the form stays as it is until then, so a failed call keeps the comment.
const props = defineProps<{
  options: DecisionOption[]
  submitting?: boolean
  // Holds every button, e.g. while something else on the page is saving.
  disabled?: boolean
  // Ties the confirmation's heading to its dialog; unique per form on a page.
  idPrefix?: string
}>()

const emit = defineEmits<{
  (event: 'submit', decision: { value: string; comment: string }): void
  (event: 'chosen', value: string | null): void
}>()

const BUTTON_COLORS: Record<DecisionTone, 'green' | 'red' | 'blue' | undefined> = {
  approve: 'green',
  reject: 'red',
  conclude: 'blue',
  neutral: undefined,
}

const formElement = ref<HTMLElement | null>(null)
// The option is looked up by value in the current `options`: when the consumer reloads the case and the
// chosen decision is gone or held back, the form goes back to the buttons instead of confirming it.
const chosenValue = ref<string | null>(null)
const chosen = computed(
  () => props.options.find((option) => option.value === chosenValue.value && !option.disabledReason) ?? null,
)
const comment = ref('')

const hasLines = computed(() => props.options.some((option) => option.line))
const titleId = computed(() => `${props.idPrefix ?? 'decision'}-confirm-title`)
const commentMissing = computed(() => Boolean(chosen.value?.commentRequired) && comment.value.trim() === '')

const setComment = (value: string) => {
  comment.value = value
}

// Focus follows the form: into the comment once a decision is chosen, back to its button on Avbryt, since
// the element that had it is gone either way.
const focusIn = async (selector: string) => {
  await nextTick()
  formElement.value?.querySelector<HTMLElement>(selector)?.focus()
}

const choose = (option: DecisionOption) => {
  chosenValue.value = option.value
  comment.value = ''
  emit('chosen', option.value)
  void focusIn('.decision-chosen textarea')
}

const cancel = () => {
  const value = chosenValue.value
  chosenValue.value = null
  emit('chosen', null)
  if (value !== null) void focusIn(`.decision-option[data-value="${value}"] button`)
}

watch(chosen, (option) => {
  if (option === null && chosenValue.value !== null) cancel()
})

const submit = () => {
  if (!chosen.value || commentMissing.value) return
  emit('submit', { value: chosen.value.value, comment: comment.value.trim() })
}

// Back to the buttons, e.g. once the consumer has shown the decided case in place.
defineExpose({ reset: cancel })
</script>

<style lang="scss" scoped>
.decision-form,
.decision-chosen {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
}

.decision-options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-12);
  margin: 0;
  padding: 0;
  list-style: none;

  &[data-layout='rows'] {
    flex-direction: column;
    gap: var(--space-8);
  }
}

.decision-option {
  display: flex;
  align-items: center;
  gap: var(--space-12);
}

// Inline-block, so it covers the whole button: hovering the disabled button lands on this, and its title shows.
.decision-option-button {
  display: inline-block;
}

.decision-option-line {
  color: var(--color-fg-subtle);
}

.decision-chosen {
  padding: var(--space-16);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);

  &[data-tone='approve'] {
    border-color: var(--color-success-emphasis);
    background-color: var(--color-success-subtle);
    color: var(--color-success-on-subtle);
  }

  &[data-tone='reject'] {
    border-color: var(--color-danger-emphasis);
    background-color: var(--color-danger-subtle);
    color: var(--color-danger-on-subtle);
  }

  &[data-tone='conclude'] {
    border-color: var(--color-warning-emphasis);
    background-color: var(--color-warning-subtle);
    color: var(--color-warning-on-subtle);
  }

  p {
    margin: 0;
  }
}

.decision-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-12);
}
</style>
